import { Badge, Table, Text } from '@radix-ui/themes'
import { installationRowClass } from '~/features/installations/constants'
import { isRetired } from '~/features/installations/service'
import { formatShortDay } from '~/utils/date'
import { MILESTONES, milestoneLabel, type InstallationTiming, type TimingRow } from '../timeline'
import { InstallationIdentityCells, InstallationIdentityHeaders } from './InstallationIdentityCells'

// Everything before the install itself; the tail milestones get their own columns so the
// planned date can sit next to the actual one.
const LEAD_MILESTONES = MILESTONES.filter((milestone) => milestone !== 'installed' && milestone !== 'indexAligned')

const SlipCell = ({ timing }: { timing: InstallationTiming }) => {
  if (timing.slipDays !== null) {
    const late = timing.slipDays > 0
    return (
      <Badge color={late ? 'red' : 'green'} variant="soft" size="1">
        {late ? `+${timing.slipDays}d` : `${timing.slipDays}d`}
      </Badge>
    )
  }
  if (timing.overdueDays !== null) {
    return (
      <Badge color="amber" variant="soft" size="1">
        +{timing.overdueDays}d overdue
      </Badge>
    )
  }
  return (
    <Text size="1" color="gray">
      —
    </Text>
  )
}

interface InstallationTimelineTableProps {
  rows: TimingRow[]
  onRowClick: (installationId: number) => void
}

export const InstallationTimelineTable = ({ rows, onRowClick }: InstallationTimelineTableProps) => (
  <Table.Root variant="surface">
    <Table.Header>
      <Table.Row>
        <InstallationIdentityHeaders />
        {LEAD_MILESTONES.map((milestone) => (
          <Table.ColumnHeaderCell key={milestone}>{milestoneLabel[milestone]}</Table.ColumnHeaderCell>
        ))}
        <Table.ColumnHeaderCell>Planned</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>Installed</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>Slip</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>{milestoneLabel.indexAligned}</Table.ColumnHeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {rows.map(({ installation, timing }) => (
        <Table.Row
          key={installation.installation_id}
          onClick={() => onRowClick(installation.installation_id)}
          className={installationRowClass({
            isStalled: installation.current_stall !== null,
            isRetired: isRetired(installation)
          })}
        >
          <InstallationIdentityCells installation={installation} />
          {LEAD_MILESTONES.map((milestone) => (
            <Table.Cell key={milestone}>
              <Text size="2" color="gray">
                {formatShortDay(timing.dates[milestone])}
              </Text>
            </Table.Cell>
          ))}
          <Table.Cell>
            <Text size="2">{formatShortDay(timing.plannedDate)}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="2" weight="medium">
              {formatShortDay(timing.dates.installed)}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <SlipCell timing={timing} />
          </Table.Cell>
          <Table.Cell>
            <Text size="2" color="gray">
              {formatShortDay(timing.dates.indexAligned)}
            </Text>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table.Body>
  </Table.Root>
)
